import React, { Suspense } from 'react';
import { CenteredSpinner } from '@components/ui/feedback/CenteredSpinner';
import { FeatureToggleBanner, useFeatureToggle } from '@features/feature-rollout-toggle';
import { useOnboardingStep } from '@features/user';
import { Box } from '@mui/material';

import { lazyWithRetry } from '../../utils/lazyWithRetry';

import { useEncounterToggleGraduation } from './hooks/useEncounterToggleGraduation';

// Lazy load both versions for code splitting and A/B testing
const EncounterViewV1 = lazyWithRetry(() => import('./components/v1/EncounterView'));
const EncounterViewV2 = lazyWithRetry(() => import('./components/v2/EncounterViewV2'));

/**
 * Encounter Page with Feature Toggle
 *
 * Main entry point for viewing/editing encounters.
 * Feature gate: feature_encounter_view_v2
 * - v1: Legacy 3-column layout with selector buttons
 * - v2: New simplified 2-column layout with form fields
 *
 * Users with the gate enabled can freely toggle between V1 and V2.
 * Users without the gate see V1 only with no toggle option.
 */
export const EncounterPage: React.FC = () => {
  const generatedFirstNote = useOnboardingStep('generated_first_note');
  const isNewUser = !generatedFirstNote;

  const toggleConfig = {
    featureKey: 'feature_encounter_view_v2',
    showBanner: true,
    isNewUser,
    bannerText: {
      messageOnNew: "You're using the new recorder",
      messageOnClassic: "We've updated the recorder",
      switchToNewLabel: 'Switch to new',
      switchToClassicLabel: 'Not ready? Switch back',
    },
  };

  const { showNew, showClassic, showBanner, switchToClassic } = useFeatureToggle(toggleConfig);
  const { handleSwitchToClassic } = useEncounterToggleGraduation();

  // Combine graduation reset with toggle switch
  const handleSwitchToClassicWithReset = () => {
    handleSwitchToClassic();
    switchToClassic();
  };

  return (
    <Box
      sx={{
        height: '100%',
        pb: showBanner ? 10 : 0, // Add padding when banner is visible
      }}
    >
      <FeatureToggleBanner
        config={toggleConfig}
        onSwitchToClassic={handleSwitchToClassicWithReset}
      />
      <Suspense fallback={<CenteredSpinner />}>
        {showNew && <EncounterViewV2 />}
        {showClassic && <EncounterViewV1 />}
      </Suspense>
    </Box>
  );
};

export default EncounterPage;
